import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { NotificationManager } from '@/components/Notification'
import { orderService } from '@/services/order'
import { trackClearedCart } from '@/store/slices/adobeAnalyticSlice'
import { getMiniCart } from '@/store/slices/cartSlice'
import { ordersActions } from '@/store/slices/orderSlice'
import { getSessionCookieKey } from '@/contexts/punchout/sessionCookieKeyStore'
import { queryClient } from '@/libs/queryClient'
import { clearCookie, clearCookieForPunchoutSystem } from '@/libs/utils/cookies'
import { linkGenerator } from '@/libs/utils/language'

import {
  selectAllowDirectShopCheckout,
} from '../selectors/configSelector'
import {
  selectHasSessionBasedShoppingCart,
  selectIsProcurementTechnicalUserCxml,
} from '../selectors/userSelector'
import { modifyPayloadIfHavingSessionBasedShoppingCart } from './cartSaga'

function* getOrdersSaga({ payload }) {
  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(payload)
    const { data } = yield orderService.getOrders({
      ...modifiedPayload,
    })

    yield put(ordersActions.getOrdersListSucceeded(data))
  } catch (error) {
    console.error(error)
    yield put(ordersActions.getOrdersListFailed())

    NotificationManager.error({
      message: 'notification.error.load',
    })
  }
}

function* getOrderSaga({ payload }) {
  try {
    const response = yield orderService.getOrder({
      orderNumber: payload.orderNumber,
    })

    yield put(ordersActions.getOrderSucceeded(response))
  } catch (error) {
    const { navigate } = payload
    console.error('Error fetching order:', error)
    yield put(ordersActions.getOrderFailed())

    NotificationManager.error({
      message: 'notification.error.somethingWentWrong',
      description: 'notification.error.nonexistentOrderNumber',
    })

    navigate({
      pathname: linkGenerator('/orders'),
      search: `?page=${payload?.currentPage ?? 1}`,
    })
  }
}

function* placeOrderSaga({ payload }) {
  try {
    const { navigate, ...orderData } = payload
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart({ orderData })
    const hasSessionBasedShoppingCart = yield select(selectHasSessionBasedShoppingCart)
    const allowDirectShopCheckout = yield select(selectAllowDirectShopCheckout)
    const isProcurementUserCxml = yield select(selectIsProcurementTechnicalUserCxml)

    const data = yield orderService.placeOrder({
      orderData: modifiedPayload.orderData,
      sessionId: modifiedPayload?.sessionId,
    })

    if (hasSessionBasedShoppingCart) {
      if (isProcurementUserCxml) {
        yield call(clearCookieForPunchoutSystem)
      } else {
        yield call(clearCookie, getSessionCookieKey())
      }
    }

    yield put(
      ordersActions.placeOrderSucceeded({
        orderData: data,
        deliveryData: orderData,
      }),
    )

    yield all([put(getMiniCart()), put(trackClearedCart())])

    queryClient.invalidateQueries({ queryKey: ['machines'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })

    navigate(linkGenerator('/confirm-order'))

    NotificationManager.success({
      message: 'notification.success.submitOrder',
    })
  } catch (error) {
    yield put(ordersActions.placeOrderFailed(error?.message))
    NotificationManager.error({
      message: 'notification.error.submitOrder',
    })
  }
}

export default function* cartSaga() {
  yield takeLatest(ordersActions.getOrdersList.toString(), getOrdersSaga)
  yield takeLatest(ordersActions.getOrder.toString(), getOrderSaga)
  yield takeLatest(ordersActions.placeOrder.toString(), placeOrderSaga)
}
